/**
 * One-shot marker that the customer has, this moment, finished the signup application and should
 * land IN the Plaid bank-linking screen rather than on the pre-active dashboard.
 *
 * NOT CURRENTLY SET: the Plaid Link application step is deprecated (Assets now advances straight to
 * pending_review), so nothing calls `markPlaidHandoff`. Kept while we try that out, so going back is
 * just restoring the call in BusinessAssets. The read side in CustomerHome is a harmless no-op.
 *
 * Deliberately sessionStorage rather than router state, because the trip from the Assets step to
 * the dashboard is not a single navigation we control:
 *
 *   1. Assets advances the application to plaid_link, which flips the customer to pre-active.
 *   2. `refetchUser` picks that status up — and it has to, or AuthRouter's `pending` branch drags
 *      them straight back to /signup.
 *   3. AuthRouter reacts to the new status while it still sees /signup, and pushes
 *      /dashboard/home ITSELF, with no state.
 *
 * That last push lands after anything Assets navigates to, so a `state: { openModal }` handoff is
 * clobbered every time (assetsToPlaid.integration.test.tsx pins the ordering). A key the
 * destination reads survives it, and survives the full provider teardown/rebuild that the
 * /signup -> /dashboard/* route change does on the way.
 *
 * Read once and cleared, which is what keeps it to that first moment: a refresh, a sign-out and
 * back, or any later visit finds nothing and lands on the dashboard exactly as it always has.
 * It is swept on logout too (SIGNUP_STORAGE_KEYS in UserContext).
 */
import {
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSessionSetItem,
} from '../shared/safeStorage';

export const PLAID_HANDOFF_KEY = 'openPlaidOnArrival';

/** Called by the Assets step as it hands the customer off to the dashboard. */
export const markPlaidHandoff = (): void => {
  // Storage unavailable (private mode / blocked): the customer just lands on the dashboard,
  // which is where they landed before this existed. Not worth failing the submit over.
  safeSessionSetItem(PLAID_HANDOFF_KEY, 'true');
};

/**
 * True exactly once per handoff. Clearing on read is the whole mechanism — the caller must act on
 * a `true` immediately, because there is no second chance to ask.
 */
export const consumePlaidHandoff = (): boolean => {
  if (safeSessionGetItem(PLAID_HANDOFF_KEY) !== 'true') return false;
  safeSessionRemoveItem(PLAID_HANDOFF_KEY);
  return true;
};
